// Phase C — Chiffrement WebCrypto (AES-GCM 256) de la file de saisie hors-ligne.
// La file est conservée dans IndexedDB, chiffrée au repos avec une clé de device
// NON extractible (jamais exposée en clair au JS/XSS), liée à la session : elle est
// détruite à la révocation de l'appareil. Purge automatique après 7 jours sans synchro.

const DB_NAME = "bvd-secure";
const DB_VER = 1;
const KEY_STORE = "keys";
const DATA_STORE = "data";
const KEY_ID = "device-queue-key";
const QUEUE_ID = "case-queue";
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours
const LEGACY_KEY = "bvd_case_queue";

const supported = typeof indexedDB !== "undefined" && !!(window.crypto && window.crypto.subtle);

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(KEY_STORE)) db.createObjectStore(KEY_STORE);
      if (!db.objectStoreNames.contains(DATA_STORE)) db.createObjectStore(DATA_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(db, store, key) {
  return new Promise((resolve, reject) => {
    const r = db.transaction(store, "readonly").objectStore(store).get(key);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
function idbPut(db, store, key, val) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    tx.objectStore(store).put(val, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
function idbDel(db, store, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    tx.objectStore(store).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getKey() {
  const db = await openDb();
  let key = await idbGet(db, KEY_STORE, KEY_ID);
  if (!key) {
    // Clé AES-GCM 256 bits NON extractible : stockée telle quelle dans IndexedDB.
    key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await idbPut(db, KEY_STORE, KEY_ID, key);
  }
  return { db, key };
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Lit et déchiffre la file, en purgeant les éléments de plus de 7 jours.
export async function readQueue() {
  if (!supported) return [];
  try {
    const { db, key } = await getKey();
    const blob = await idbGet(db, DATA_STORE, QUEUE_ID);
    if (!blob) return [];
    const iv = new Uint8Array(blob.iv);
    const buf = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, blob.data);
    let items = JSON.parse(decoder.decode(buf));
    if (!Array.isArray(items)) return [];
    const now = Date.now();
    const fresh = items.filter((it) => now - (it.queued_at || now) < MAX_AGE_MS);
    if (fresh.length !== items.length) await writeQueue(fresh);
    return fresh;
  } catch {
    return [];
  }
}

export async function writeQueue(items) {
  if (!supported) return;
  const { db, key } = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoder.encode(JSON.stringify(items)));
  await idbPut(db, DATA_STORE, QUEUE_ID, { iv: Array.from(iv), data });
}

export async function enqueue(rec) {
  const items = await readQueue();
  items.push({ rec, queued_at: Date.now() });
  await writeQueue(items);
  return items;
}

export async function queueCount() {
  return (await readQueue()).length;
}

// Révocation de l'appareil : détruit la clé et la file chiffrée locales.
export async function wipeDevice() {
  try {
    const db = await openDb();
    await idbDel(db, DATA_STORE, QUEUE_ID);
    await idbDel(db, KEY_STORE, KEY_ID);
  } catch { /* ignore */ }
  try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
}

// Migration unique de l'ancienne file en clair (localStorage) vers le stockage chiffré.
export async function migrateLegacy() {
  if (!supported) return;
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return;
    const recs = JSON.parse(raw);
    if (Array.isArray(recs) && recs.length) {
      const existing = await readQueue();
      const now = Date.now();
      await writeQueue([...existing, ...recs.map((rec) => ({ rec, queued_at: now }))]);
    }
    localStorage.removeItem(LEGACY_KEY);
  } catch { /* ignore */ }
}
